import { asClearableString, makeDomainSave } from './domainSave'
import { request } from '@/lib/api'

/**
 * Доступ к карточке контрагента (тикет #70, ТЗ §4.2).
 *
 * ## Почему отдельно от `card/api.ts`
 *
 * `card/api.ts` — универсальный слой каркаса #66: он читает и пишет **одну запись** через
 * механизм списков и не знает, какая это сущность. Здесь наоборот: наборы, которые есть
 * только у контрагента (контакты, банковские счета, договоры), и сводки, считающиеся из
 * нескольких контекстов. Складывать их туда значило бы, что каркас начинает знать про
 * контрагента — ровно то, ради устранения чего он и написан данными.
 *
 * ## Что читается отсюда, а что через каркас
 *
 * Поля самой карточки (имя, ИНН, лимит) читает и пишет каркас через `/lists`: там уже
 * работают полевые права и область видимости. Здесь — только то, чего плоская строка
 * списка дать не может. Второй путь чтения тех же полей означал бы два реестра колонок,
 * которые разойдутся.
 */

/** Реквизиты: шире строки списка — адреса, ОГРН, ОКПО в каталог не объявлены. */
export interface Requisites {
  id: string
  name: string
  legalName: string | null
  kind: string
  relation: string
  inn: string | null
  kpp: string | null
  ogrn: string | null
  okpo: string | null
  legalAddress: string | null
  actualAddress: string | null
  email: string | null
  phone: string | null
  website: string | null
  industry: string | null
  territoryId: string | null
  responsibleEmployeeId: string | null
  priceListId: string | null
  paymentTermsDays: number
  blocked: boolean
  blockReason: string | null
}

/** Контактное лицо. `consentGiven` — флаг, сам документ живёт в комплаенсе. */
export interface CardContact {
  id: string
  fullName: string
  lastName: string
  firstName: string | null
  middleName: string | null
  jobTitle: string | null
  email: string | null
  phone: string | null
  mobilePhone: string | null
  telegram: string | null
  primary: boolean
  consentGiven: boolean
}

/** Банковский счёт: полный номер для сверки, маска для экрана. */
export interface BankAccount {
  id: string
  bankName: string
  bik: string
  accountNumber: string
  maskedAccountNumber: string
  corrAccount: string | null
  currency: string
  primary: boolean
}

/** Договор. Только чтение: `ContractService` в проекте нет — это #73. */
export interface CardContract {
  id: string
  number: string
  contractType: string
  status: string
  currency: string
  signedOn: string | null
  validFrom: string | null
  validUntil: string | null
  amount: number | null
  paymentTermsDays: number
  usableToday: boolean
}

/**
 * Ageing **одной валюты**.
 *
 * Валюта — часть записи, а не контекст, который клиент помнит отдельно: `total` складывает
 * пять чисел, и это сложение верно только внутри одной валюты.
 */
export interface AgeingLine {
  counterpartyId: string
  currency: string
  current: number
  upTo30Days: number
  upTo60Days: number
  upTo90Days: number
  beyond90Days: number
}

/**
 * Финансовая вкладка.
 *
 * `creditLimit === null` при `unlimitedCredit` — это «лимита нет» (решение по доверенному
 * клиенту), а не «не заполнено». Лимит в ноль означает противоположное: кредит запрещён.
 */
export interface Finance {
  counterpartyId: string
  creditLimit: number | null
  unlimitedCredit: boolean
  outstandingDebt: number
  availableCredit: number | null
  creditState: 'unlimited' | 'within' | 'exceeded'
  blocked: boolean
  blockReason: string | null
  paymentTermsDays: number
  ageingByCurrency: AgeingLine[]
  contracts: CardContract[]
}

/** Похожая карточка и то, чем именно она похожа. */
export interface DuplicateMatch {
  id: string
  name: string
  inn: string | null
  kpp: string | null
  email: string | null
  phone: string | null
  blocked: boolean
  /** Причины: `inn`, `inn_kpp`, `phone`, `contact_phone`, `email`, `contact_email`. */
  matchedOn: string[]
}

/**
 * Результат проверки на дубли.
 *
 * `blocking` отделяет «посмотрите» от «так нельзя»: сохранение отвергнет только точное
 * совпадение пары `(inn, kpp)`. Смешать это с совпадением телефона значит либо пугать там,
 * где всё в порядке, либо не предупредить там, где сохранение сейчас упадёт.
 */
export interface Duplicates {
  matches: DuplicateMatch[]
  blocking: boolean
}

/** Активность в ленте взаимодействий — та же форма, что отдаёт #67. */
export interface TimelineActivity {
  id: string
  type: string
  status: string
  subject: string | null
  body: string | null
  entityType: string
  entityId: string
  direction: string | null
  durationSeconds: number | null
  scheduledAt: string | null
  dueAt: string | null
  completedAt: string | null
  overdue: boolean
  createdAt: string
}

/** Лента вместе со счётчиком: «сколько раз мы его трогали» спрашивают первым. */
export interface Timeline {
  counterpartyId: string
  touchCount: number
  activities: TimelineActivity[]
}

const BASE = '/sales/counterparties'

export function fetchRequisites(id: string, signal?: AbortSignal): Promise<Requisites> {
  return request<Requisites>(`${BASE}/${id}/requisites`, { signal })
}

export function fetchContacts(id: string, signal?: AbortSignal): Promise<CardContact[]> {
  return request<CardContact[]>(`${BASE}/${id}/contacts`, { signal })
}

export function fetchBankAccounts(id: string, signal?: AbortSignal): Promise<BankAccount[]> {
  return request<BankAccount[]>(`${BASE}/${id}/bank-accounts`, { signal })
}

export function fetchFinance(id: string, signal?: AbortSignal): Promise<Finance> {
  return request<Finance>(`${BASE}/${id}/finance`, { signal })
}

/**
 * Лента касаний.
 *
 * Зовёт эндпоинт #67, а не свою копию: счётчик касаний обязан быть одной цифрой. Две
 * реализации разошлись бы молча, и разошлись бы именно там, где на цифру смотрят.
 */
export function fetchTimeline(id: string, limit = 50, signal?: AbortSignal): Promise<Timeline> {
  return request<Timeline>(`${BASE}/${id}/timeline?limit=${limit}`, { signal })
}

/**
 * Поиск дублей перед сохранением.
 *
 * Пустой запрос (ни одного заполненного признака) до сервера **не идёт**: он вернул бы
 * пустой список, а это неотличимо от «дублей нет» — то есть форма показала бы «всё
 * чисто», не проверив ничего.
 */
export function fetchDuplicates(
  criteria: { inn?: string; kpp?: string; phone?: string; email?: string },
  signal?: AbortSignal,
): Promise<Duplicates> {
  const params = new URLSearchParams()
  if (criteria.inn?.trim()) params.set('inn', criteria.inn.trim())
  if (criteria.kpp?.trim()) params.set('kpp', criteria.kpp.trim())
  if (criteria.phone?.trim()) params.set('phone', criteria.phone.trim())
  if (criteria.email?.trim()) params.set('email', criteria.email.trim())

  if (!params.has('inn') && !params.has('phone') && !params.has('email')) {
    return Promise.resolve({ matches: [], blocking: false })
  }
  return request<Duplicates>(`${BASE}/duplicates?${params.toString()}`, { signal })
}

/**
 * Что уходит на сервер при заведении карточки.
 *
 * `paymentTermsDays` не входит: ноль означает «оплата вперёд», а не «не заполнено», и
 * поле, которое форма шлёт пустым, установило бы предоплату каждому новому клиенту.
 * Отсрочка правится на карточке, где видно, что она значит.
 */
export interface CreateCounterpartyRequest {
  name: string
  kind?: string
  relation?: string
  legalName?: string
  inn?: string
  kpp?: string
  ogrn?: string
  okpo?: string
  legalAddress?: string
  actualAddress?: string
  email?: string
  phone?: string
  website?: string
  industry?: string
  /** Поля физлица (#99): уходят только при kind = individual. */
  lastName?: string
  firstName?: string
  middleName?: string
  birthDate?: string
  consentGiven?: boolean
}

/** Заведённая карточка вместе с подсказкой о похожих. */
export interface CounterpartyIntake {
  counterparty: Requisites
  possibleDuplicates: DuplicateMatch[]
}

/**
 * Заводит контрагента.
 *
 * Точный дубль пары `(inn, kpp)` сервер отвергает кодом `counterparty.duplicate`, и его
 * сообщение называет существующую карточку. Клиент этот случай не предсказывает: своя
 * проверка завела бы второе место, где решается, что считать дублем.
 */
export function createCounterparty(
  body: CreateCounterpartyRequest,
): Promise<CounterpartyIntake> {
  return request<CounterpartyIntake>(BASE, { method: 'POST', body })
}

/**
 * Есть ли что искать.
 *
 * Отдельной функцией, потому что это же условие нужно экрану: пока ни один признак не
 * заполнен, панель дублей не должна говорить «дублей не найдено». «Не искали» и «искали и
 * не нашли» — разные утверждения, и первое, выданное за второе, разрешает сохранить дубль
 * с чистой совестью.
 *
 * КПП сюда намеренно не входит: он уточняет ИНН, а сам по себе ничего не находит — все
 * филиалы всех юрлиц делят несколько сотен КПП.
 */
export function hasSearchableCriteria(criteria: {
  inn?: string
  phone?: string
  email?: string
}): boolean {
  return Boolean(
    criteria.inn?.trim() || criteria.phone?.trim() || criteria.email?.trim(),
  )
}

// ------------------------------------------------------------------ правка и слияние (#99)

/**
 * Сохранение карточки контрагента доменным PUT с версией (#99).
 *
 * Кредитный лимит уходит строкой: пустая снимает лимит («без лимита», а не ноль). Блокировка
 * (`is_blocked`) не правится полем — у неё обязательная причина и своя операция, поэтому
 * колонки здесь нет, и правка её отвергается, а не теряется.
 */
export const saveCounterparty = makeDomainSave((id) => `/sales/counterparties/${id}`, {
  name: { field: 'name', convert: asClearableString },
  kind: 'kind',
  relation: 'relation',
  legal_name: { field: 'legalName', convert: asClearableString },
  inn: { field: 'inn', convert: asClearableString },
  kpp: { field: 'kpp', convert: asClearableString },
  ogrn: { field: 'ogrn', convert: asClearableString },
  okpo: { field: 'okpo', convert: asClearableString },
  legal_address: { field: 'legalAddress', convert: asClearableString },
  actual_address: { field: 'actualAddress', convert: asClearableString },
  email: { field: 'email', convert: asClearableString },
  phone: { field: 'phone', convert: asClearableString },
  website: { field: 'website', convert: asClearableString },
  industry: { field: 'industry', convert: asClearableString },
  credit_limit: { field: 'creditLimit', convert: asClearableString },
  payment_terms_days: { field: 'paymentTermsDays', convert: (v) => (v === '' || v === null ? null : Number(v)) },
  responsible_employee_id: 'responsibleEmployeeId',
  last_name: { field: 'lastName', convert: asClearableString },
  first_name: { field: 'firstName', convert: asClearableString },
  middle_name: { field: 'middleName', convert: asClearableString },
  birth_date: { field: 'birthDate', convert: asClearableString },
})

export interface MergePreview {
  survivorId: string
  survivorName: string
  duplicateId: string
  duplicateName: string
  /** `таблица.колонка` → сколько строк переедет. */
  moved: Record<string, number>
  /** `таблица.колонка` → сколько строк истории учёта мешают слиянию. */
  blockers: Record<string, number>
  /** Почему карточки несовместимы вовсе (разные ИНН, филиалы). */
  incompatible: string[]
}

export function fetchMergePreview(survivorId: string, duplicateId: string, signal?: AbortSignal): Promise<MergePreview> {
  return request<MergePreview>(
    `/sales/counterparties/${survivorId}/merge-preview?duplicateId=${encodeURIComponent(duplicateId)}`,
    { signal },
  )
}

export function mergeCounterparty(survivorId: string, duplicateId: string): Promise<MergePreview> {
  return request<MergePreview>(`/sales/counterparties/${survivorId}/merge`, {
    method: 'POST',
    body: { duplicateId },
  })
}

/**
 * Удаление контрагента (#99): мягкое и только без истории.
 *
 * Клиента с открытыми сделками, выставленными счетами, платежами, договорами или складскими
 * операциями сервер не удалит (`counterparty.delete_blocked` с перечнем) — его блокируют, а не
 * удаляют: иначе он исчез бы из списков, а его документы остались. Клиент этот запрет не
 * предсказывает: второе место, где решается «можно ли удалить», разошлось бы с первым.
 */
export function deleteCounterparty(id: string): Promise<void> {
  return request<void>(`${BASE}/${id}`, { method: 'DELETE' })
}

